import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlarmClock, Bell, CalendarDays, CheckCheck, Gift, Handshake, Heart, MessageCircle, Package, Radio, Repeat2, Settings2, ShieldCheck, Star, Ticket, Trash2, Trophy, UserPlus, Wallet, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { notificationsApi, notificationTarget, relTime } from "@/lib/accountApi";
import { Empty } from "@/components/partner/PartnerShell";

const CAT_TONE = {
  booking: "bg-emerald-100 text-emerald-800",
  reminders: "bg-amber-100 text-amber-800",
  pickup: "bg-orange-100 text-orange-800",
  casting: "bg-violet-100 text-violet-800",
  promotions: "bg-pink-100 text-pink-800",
  posts: "bg-sky-100 text-sky-800",
  system: "bg-muted text-foreground/70",
};

const FILTERS = [["all", "All"], ["unread", "Unread"], ["social", "Social"], ["orders", "Orders"], ["reminders", "Reminders"]];
const isSocial = (t) => /^(new_follower|pulse_|new_post|group|gift|coins|collab|community|live|access|studio)/.test(t || "");
const isOrder = (t) => /^(booking|order_|events|payout|deposit|dispute|chargeback|shop_|flight|review_prompt)/.test(t || "");
const isReminder = (t) => /reminder/.test(t || "");
const applyFilter = (items, f) => (f === "unread" ? items.filter((n) => !n.read) : f === "social" ? items.filter((n) => isSocial(n.type)) : f === "orders" ? items.filter((n) => isOrder(n.type)) : f === "reminders" ? items.filter((n) => isReminder(n.type)) : items);
const TYPE_ICON = [
  [/^new_follower/, UserPlus], [/^pulse_like/, Heart], [/^pulse_comment|^pulse_mention|^group/, MessageCircle], [/^pulse_repost/, Repeat2],
  [/^collab/, Handshake], [/^community/, Trophy], [/^live/, Radio], [/^gift|^coins/, Gift], [/^shop_review|^review/, Star],
  [/^order|^shop_|^booking/, Package], [/^events/, Ticket], [/^payout|^deposit/, Wallet], [/reminder/, AlarmClock], [/^security|^verification|^age_check/, ShieldCheck],
];
const iconFor = (t) => (TYPE_ICON.find(([rx]) => rx.test(t || "")) || [null, Bell])[1];
function dayGroups(items) {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const y = new Date(start.getTime() - 86400000);
  const wk = new Date(start.getTime() - 6 * 86400000);
  const g = [["Today", []], ["Yesterday", []], ["This week", []], ["Earlier", []]];
  items.forEach((n) => { const d = new Date(n.delivered_at || n.created_at); (d >= start ? g[0] : d >= y ? g[1] : d >= wk ? g[2] : g[3])[1].push(n); });
  return g.filter(([, list]) => list.length);
}

function NotifRow({ n, onOpen, onRemove }) {
  const Icon = iconFor(n.type);
  return (
    <li className="group relative">
      <button type="button" onClick={() => onOpen(n)} data-testid={"notif-" + n.id} className={"flex w-full items-start gap-3 px-4 py-4 pr-12 text-left transition-colors hover:bg-muted/50 " + (n.read ? "" : "bg-primary/5")}>
        <span className={"mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full " + (CAT_TONE[n.category] || CAT_TONE.system)}><Icon size={16} /></span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className={"truncate " + (n.read ? "font-medium" : "font-semibold")}>{n.title}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{relTime(n.created_at)}</span>
          </span>
          {n.body && <span className="mt-0.5 block text-sm text-muted-foreground">{n.body}</span>}
        </span>
        {!n.read && <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: "var(--fable)" }} aria-label="Unread" />}
      </button>
      <button type="button" onClick={() => onRemove(n)} aria-label="Remove notification" data-testid={"notif-remove-" + n.id} className="absolute right-3 top-4 rounded-full p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100"><X size={15} /></button>
    </li>
  );
}

function AgendaCard({ a }) {
  const when = a.at ? new Date(a.at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : [a.date && new Date(a.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }), a.time].filter(Boolean).join(" · ");
  const to = notificationTarget({ action_url: a.action_url });
  const body = (
    <div className="min-w-[220px] max-w-xs shrink-0 rounded-2xl border border-border bg-card p-4" data-testid={`agenda-${a.id}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{a.context || a.kind}</p>
      <p className="mt-1 truncate font-semibold">{a.title}</p>
      <p className="text-sm text-muted-foreground">{when}{a.place ? ` · ${a.place}` : ""}</p>
      {a.note && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{a.note}</p>}
    </div>
  );
  return to && !/^https?:/.test(to) ? <Link to={to}>{body}</Link> : body;
}

export default function Notifications() {
  usePageMeta("Notifications", "Booking confirmations, reminders and updates from Fable +.");
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => notificationsApi.list().then(setData).catch(() => setData(null)), []);
  useEffect(() => {
    load();
  }, [load]);

  const open = async (n) => {
    if (!n.read) {
      setData((d) => ({ ...d, unread: Math.max(0, (d.unread || 1) - 1), items: d.items.map((x) => (x.id === n.id ? { ...x, read: true } : x)) }));
      notificationsApi.read(n.id).catch(() => null);
    }
    const to = notificationTarget(n);
    if (!to) return;
    if (/^https?:/.test(to)) window.open(to, "_blank", "noopener");
    else navigate(to);
  };

  const readAll = async () => {
    try {
      await notificationsApi.readAll();
      setData((d) => ({ ...d, unread: 0, items: d.items.map((x) => ({ ...x, read: true })) }));
    } catch (e) {
      toast.error(e.message || "Couldn't update that.");
    }
  };

  const remove = (n) => {
    setData((d) => ({ ...d, unread: Math.max(0, (d.unread || 0) - (n.read ? 0 : 1)), items: d.items.filter((x) => x.id !== n.id) }));
    api("/notifications/" + n.id, { method: "DELETE" }).catch(() => null);
  };
  const clearRead = async () => {
    try { const r = await api("/notifications/clear", { method: "POST" }); setData((d) => ({ ...d, items: d.items.filter((x) => !x.read) })); toast(r.cleared ? "Cleared " + r.cleared + " read notification" + (r.cleared === 1 ? "" : "s") : "Nothing to clear"); } catch (e) { toast.error(e.message || "Couldn't clear."); }
  };
  const all = data?.items || [];
  const items = applyFilter(all, filter);
  const groups = dayGroups(items);
  const agenda = data?.agenda || [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif-display text-3xl font-bold">Notifications</h1>
          <p className="mt-1 text-muted-foreground">{data?.unread ? `${data.unread} unread` : "You're all caught up."}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={readAll} disabled={!data?.unread} data-testid="notif-read-all"><CheckCheck size={15} className="mr-1.5" /> Mark all read</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={clearRead} disabled={!all.some((x) => x.read)} data-testid="notif-clear"><Trash2 size={15} className="mr-1.5" /> Clear read</Button>
          <Link to="/profile?tab=notifications"><Button variant="outline" size="sm" className="rounded-full" data-testid="notif-prefs"><Settings2 size={15} className="mr-1.5" /> Settings</Button></Link>
        </div>
      </div>

      <div className="mt-5 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" role="tablist" aria-label="Filter notifications">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} data-testid={"notif-filter-" + k} className={"whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold " + (filter === k ? "border-transparent bg-[#E0007A] text-white" : "border-border bg-card hover:bg-muted")}>{l}{k === "unread" && data?.unread ? " · " + data.unread : ""}</button>
        ))}
      </div>

      {(filter === "all" || filter === "reminders") && agenda.length > 0 && (
        <section className="mt-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold"><CalendarDays size={16} /> This week</h2>
          <div className="mt-3 flex gap-3 overflow-x-auto pb-2">{agenda.map((a) => <AgendaCard key={a.id} a={a} />)}</div>
        </section>
      )}

      <section className="mt-6" data-testid="notif-list">
        {data === undefined ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
        ) : data === null ? (
          <p className="py-10 text-center text-muted-foreground">Couldn't load notifications. Please try again shortly.</p>
        ) : items.length === 0 ? (
          <Empty title={filter === "all" ? "Nothing here yet" : "Nothing in this filter"} body="Booking confirmations, reminders and replies will show up here." action={<Link to="/marketplace"><Button className="rounded-full">Explore the marketplace</Button></Link>} />
        ) : (
          <div className="space-y-6">
            {groups.map(([label, list]) => (
              <div key={label}>
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</h2>
                <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">{list.map((n) => <NotifRow key={n.id} n={n} onOpen={open} onRemove={remove} />)}</ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
